import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Moon, Mail, Lock, Eye, EyeOff, Sparkles, X } from 'lucide-react';
import { heroImage } from '../mock';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Mohon isi email dan password.');
      return;
    }
    setLoading(true);
    login(email, password).then((res) => {
      setLoading(false);
      if (res.ok) {
        toast.success('Selamat datang kembali!', { description: 'Barakallahu fiik.' });
        navigate(res.role === 'admin' ? '/admin' : '/dashboard');
      } else {
        toast.error(res.message || 'Login gagal.');
      }
    });
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left: image + tagline */}
      <div className="relative md:w-1/2 min-h-[38vh] md:min-h-screen overflow-hidden">
        <img src={heroImage} alt="Masjid" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(6,42,31,0.15) 0%, rgba(6,42,31,0.55) 70%, rgba(6,42,31,0.9) 100%)' }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-7 md:p-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center shadow-lg">
              <Moon className="w-5 h-5 text-[#0a3b2c]" />
            </div>
            <span className="text-white text-lg font-medium">Pahala Digital</span>
          </div>
          <div className="max-w-md">
            <h1 className="font-serif-display text-[#e3ba63] text-4xl md:text-5xl leading-tight mb-4">
              Menebar Kebaikan, Menjemput Keberkahan
            </h1>
            <p className="text-white/80 text-sm md:text-base leading-relaxed">
              Portal member eksklusif untuk akses Paket Eksklusif 7.000+ Konten Islamic. Daftar sebagai mitra, lalu login setelah akunmu di-ACC admin.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="relative md:w-1/2 min-h-[62vh] md:min-h-screen islamic-pattern flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md bg-[#0d4433]/70 backdrop-blur-sm border border-[#d9a94e]/25 rounded-3xl p-8 md:p-10 shadow-2xl animate-fade-up">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#d9a94e]" />
            <span className="text-[#d9a94e] text-xs font-semibold tracking-[0.25em]">MEMBER AREA</span>
          </div>
          <h2 className="font-serif-display text-white text-4xl mb-2">Masuk Akun</h2>
          <p className="text-white/60 text-sm mb-7">Silakan masuk untuk mengakses kontenmu.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-white/85 text-sm font-medium mb-2">Email / Username</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#d9a94e]" />
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email atau username"
                  className="w-full bg-[#072a20] border border-[#d9a94e]/25 rounded-xl py-3 pl-11 pr-4 text-white placeholder-white/35 text-sm outline-none focus:border-[#d9a94e] transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-white/85 text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#d9a94e]" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#072a20] border border-[#d9a94e]/25 rounded-xl py-3 pl-11 pr-11 text-white placeholder-white/35 text-sm outline-none focus:border-[#d9a94e] transition-colors"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full gold-gradient text-[#0a3b2c] font-semibold py-3.5 rounded-xl hover:brightness-105 active:scale-[0.99] transition disabled:opacity-70"
            >
              {loading ? 'Memproses...' : 'Masuk'}
            </button>
          </form>

          <p className="text-center text-white/60 text-sm mt-6">
            Belum punya akun?{' '}
            <button onClick={() => setShowRegister(true)} className="text-[#d9a94e] font-semibold hover:underline">
              Daftar untuk akses konten
            </button>
          </p>
        </div>
      </div>

      {showRegister && <RegisterModal onClose={() => setShowRegister(false)} register={register} />}
    </div>
  );
}

function RegisterModal({ onClose, register }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.password) {
      toast.error('Mohon lengkapi semua data.');
      return;
    }
    setLoading(true);
    register(form).then((res) => {
      setLoading(false);
      if (res.ok) {
        toast.success('Pendaftaran berhasil!', { description: 'Akunmu menunggu ACC admin.' });
        onClose();
      } else {
        toast.error(res.message);
      }
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0d4433] border border-[#d9a94e]/25 rounded-3xl p-8 shadow-2xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#d9a94e]" />
            <span className="text-[#d9a94e] text-xs font-semibold tracking-[0.25em]">DAFTAR MITRA</span>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <h2 className="font-serif-display text-white text-3xl mb-1">Buat Akun</h2>
        <p className="text-white/60 text-sm mb-6">Setelah daftar, akunmu akan di-ACC admin terlebih dahulu.</p>
        <form onSubmit={submit} className="space-y-4">
          {[
            { k: 'name', label: 'Nama', type: 'text', ph: 'Nama lengkap' },
            { k: 'email', label: 'Email', type: 'email', ph: 'email@contoh.com' },
            { k: 'phone', label: 'No. WhatsApp', type: 'tel', ph: '08xxxxxxxxxx' },
            { k: 'password', label: 'Password', type: 'password', ph: 'buat password' },
          ].map((f) => (
            <div key={f.k}>
              <label className="block text-white/85 text-sm font-medium mb-1.5">{f.label}</label>
              <input
                type={f.type}
                value={form[f.k]}
                onChange={set(f.k)}
                placeholder={f.ph}
                className="w-full bg-[#072a20] border border-[#d9a94e]/25 rounded-xl py-2.5 px-4 text-white placeholder-white/35 text-sm outline-none focus:border-[#d9a94e] transition-colors"
              />
            </div>
          ))}
          <button type="submit" disabled={loading} className="w-full gold-gradient text-[#0a3b2c] font-semibold py-3 rounded-xl hover:brightness-105 transition disabled:opacity-70">
            {loading ? 'Memproses...' : 'Daftar Sekarang'}
          </button>
        </form>
      </div>
    </div>
  );
}
